import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { api, getToken, setToken, whenSessionEnds, type StaffMe } from "./api";

interface Session {
  me: StaffMe | null;
  loading: boolean;
  /** Step 1: password. Resolves with where the OTP went (masked mobile). */
  sendOtp: (email: string, password: string) => Promise<string>;
  /** Step 2: the OTP. */
  verify: (email: string, code: string) => Promise<void>;
  logOut: () => void;
  ended: boolean;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<StaffMe | null>(null);
  const [loading, setLoading] = useState(Boolean(getToken()));
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    whenSessionEnds(() => {
      setMe(null);
      setEnded(true);
    });
    if (!getToken()) return;
    api<StaffMe>("/admin/me")
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, []);

  const sendOtp = useCallback(async (email: string, password: string) => {
    const r = await api<{ otp_sent_to: string }>("/admin/auth/login", { method: "POST", body: { email, password } });
    return r.otp_sent_to;
  }, []);

  const verify = useCallback(async (email: string, code: string) => {
    const r = await api<{ token: string; me: StaffMe }>("/admin/auth/verify", { method: "POST", body: { email, code } });
    setToken(r.token);
    setEnded(false);
    setMe(r.me);
  }, []);

  const logOut = useCallback(() => {
    setToken(null);
    setMe(null);
  }, []);

  const value = useMemo(() => ({ me, loading, sendOtp, verify, logOut, ended }), [me, loading, sendOtp, verify, logOut, ended]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}
